import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ShoppingCart,
  Store,
  Smartphone,
  TrendingUp,
  Security,
  Speed,
  Support,
  CheckCircle,
  Star,
  ExpandMore,
  Palette,
  Settings,
  Analytics,
  Payment,
  Inventory,
  LocalShipping
} from '@mui/icons-material';
import Header from './components/Header';
import Footer from './components/Footer';
import PublicAiChatWidget from './components/PublicAiChatWidget';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';
import { nodeApi, mainApi } from '../../config/apiClient';
import { usePublicRegistrationFlow } from '../../contexts/PublicRegistrationFlowContext';
import {
  StandardMarketingModal,
  marketingModalTextFieldSx,
} from './components/StandardMarketingModal';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.primary, // BRAND'den
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.9) 0%, rgba(25, 118, 210, 0.8) 100%)',
  padding: theme.spacing(8, 0),
  minHeight: '35vh',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const PricingCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[12],
  },
    '&.popular': {
      border: `3px solid ${theme.palette.primary.main}`,
      paddingTop: theme.spacing(4), // Kutunun üst kısmını yukarı doğru uzat
      '&::before': {
        content: '"En Popüler"',
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: '4px 16px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        zIndex: 1
      }
    }
}));

const StatsSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: '1px solid rgba(255, 255, 255, 0.2)'
}));

const Ecommerce: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const pricingRef = useRef<HTMLDivElement>(null);

  // Modal state'leri
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Form state'i
  const [formData, setFormData] = useState({
    nameSurname: '',
    email: '',
    phone: '',
    selectedPlan: ''
  });

  const handleScrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Form input değişikliklerini handle et
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Modal açma fonksiyonu
  const handleOpenContactModal = (planName: string) => {
    setFormData(prev => ({ ...prev, selectedPlan: planName }));
    setContactModalOpen(true);
  };

  // Modal kapatma fonksiyonları
  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  // Form gönderme fonksiyonu
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form gönderildi:', formData);
    
    setIsSubmitting(true);
    
    try {
      // İletişim talebini .NET API'ye kaydet (anonim, public)
      await mainApi.post('/api/ContactLeads/create', {
        nameSurname: formData.nameSurname,
        email: formData.email,
        phone: formData.phone,
        selectedPlan: formData.selectedPlan,
        source: 'ecommerce_pricing'
      });

      console.log("Veri başarıyla kaydedildi!");

      // Başarı modalını aç
      setSuccessModalOpen(true);
      setContactModalOpen(false);

      // Belirli kullanıcılara bildirim gönder
      try {
        console.log("Belirli kullanıcılara bildirim gönderiliyor...");

        nodeApi.post('/api/send-specific-notification', {
          title: 'Yeni E-Ticaret Talebi',
          body: `${formData.nameSurname} adlı kişi ${formData.selectedPlan} planı için iletişime geçmek istiyor. Tel: ${formData.phone}, E-posta: ${formData.email}`,
          source: 'ecommerce_pricing'
        })
        .then(response => {
          console.log("Bildirim gönderme sonucu:", response.data);
        })
        .catch(notificationError => {
          console.error("Bildirim gönderilirken hata:", notificationError);
        });
      } catch (notificationError) {
        console.error("Bildirim gönderimi sırasında hata:", notificationError);
      }

      // Form verilerini sıfırla
      setFormData({
        nameSurname: '',
        email: '',
        phone: '',
        selectedPlan: ''
      });
    } catch (error) {
      // Hata olursa bildirim göster
      console.error('Form gönderme hatası:', error);
      setNotification({
        open: true,
        message: 'Form gönderilirken bir hata oluştu: ' + (error as Error).message,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: <Store />,
      title: t('ecommerce.features.professionalSite.title'),
      description: t('ecommerce.features.professionalSite.description'),
      color: BRAND.colors.secondary
    },
    {
      icon: <Smartphone />,
      title: t('ecommerce.features.mobileApp.title'),
      description: t('ecommerce.features.mobileApp.description'),
      color: '#2E7D32'
    },
    {
      icon: <Settings />,
      title: t('ecommerce.features.easyManagement.title'),
      description: t('ecommerce.features.easyManagement.description'),
      color: '#ED6C02'
    },
    {
      icon: <Payment />,
      title: t('ecommerce.features.securePayment.title'),
      description: t('ecommerce.features.securePayment.description'),
      color: '#9C27B0'
    },
    {
      icon: <Analytics />,
      title: t('ecommerce.features.detailedReporting.title'),
      description: t('ecommerce.features.detailedReporting.description'),
      color: '#D32F2F'
    },
    {
      icon: <LocalShipping />,
      title: t('ecommerce.features.cargoIntegration.title'),
      description: t('ecommerce.features.cargoIntegration.description'),
      color: '#1565C0'
    }
  ];

  const pricingPlans = [
    {
      name: t('ecommerce.pricing.starter.name'),
      price: t('ecommerce.pricing.starter.price'),
      period: t('ecommerce.pricing.starter.period'),
      description: t('ecommerce.pricing.starter.description'),
      features: t('ecommerce.pricing.starter.features', { returnObjects: true }) as string[],
      buttonText: t('ecommerce.pricing.starter.buttonText'),
      action: 'register',
      popular: false
    },
    {
      name: t('ecommerce.pricing.professional.name'),
      price: t('ecommerce.pricing.professional.price'),
      period: t('ecommerce.pricing.professional.period'),
      description: t('ecommerce.pricing.professional.description'),
      features: t('ecommerce.pricing.professional.features', { returnObjects: true }) as string[],
      buttonText: t('ecommerce.pricing.professional.buttonText'),
      action: 'register',
      popular: true
    },
    {
      name: t('ecommerce.pricing.enterprise.name'),
      price: t('ecommerce.pricing.enterprise.price'),
      period: t('ecommerce.pricing.enterprise.period'),
      description: t('ecommerce.pricing.enterprise.description'),
      features: t('ecommerce.pricing.enterprise.features', { returnObjects: true }) as string[],
      buttonText: t('ecommerce.pricing.enterprise.buttonText'),
      action: 'contact',
      popular: false
    }
  ];

  const stats = [
    { number: '1000+', label: t('ecommerce.stats.activeSites') },
    { number: '5M+', label: t('ecommerce.stats.monthlyVolume') },
    { number: '99.9%', label: t('ecommerce.stats.uptime') },
    { number: '7/24', label: t('ecommerce.stats.support') }
  ];

  const faqs = [
    {
      question: t('ecommerce.faq.q1.question'),
      answer: t('ecommerce.faq.q1.answer')
    },
    {
      question: t('ecommerce.faq.q2.question'),
      answer: t('ecommerce.faq.q2.answer')
    },
    {
      question: t('ecommerce.faq.q3.question'),
      answer: t('ecommerce.faq.q3.answer')
    },
    {
      question: t('ecommerce.faq.q4.question'),
      answer: t('ecommerce.faq.q4.answer')
    },
    {
      question: t('ecommerce.faq.q5.question'),
      answer: t('ecommerce.faq.q5.answer')
    }
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>{t('ecommerce.meta.title')}</title>
        <meta name="description" content={t('ecommerce.meta.description')} />
        <meta name="keywords" content={t('ecommerce.meta.keywords')} />
      </Helmet>
      <Header />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {t('ecommerce.hero.title')}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' },
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                whiteSpace: 'pre-line'
              }}
            >
              {t('ecommerce.hero.subtitle')}
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  mr: 2,
                  mb: { xs: 2, sm: 0 },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => openRoleSelection()}
              >
                {t('ecommerce.hero.tryFreeButton')}
              </Button>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                {t('ecommerce.hero.pricingButton')}
              </Button>
            </Box>

            {/* Quick Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={handleScrollToPricing}
              >
                Fiyatlar
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('features');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Özellikler
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('faq');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                SSS
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('cta');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Başlayın
              </Button>
            </Box>

            {/* Stats */}
            <StatsSection>
              <Grid container spacing={4}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box textAlign="center">
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800, 
                          color: 'white',
                          fontSize: { xs: '1rem', md: '1.8rem' }
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: { xs: '0.7rem', md: '0.8rem' }
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </StatsSection>
          </Container>
        </HeroSection>

        {/* Pricing Section */}
        <Box sx={{ py: 8, backgroundColor: 'white' }} ref={pricingRef}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('ecommerce.pricing.title')}
            </Typography>
            <Grid container spacing={4}>
              {pricingPlans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <PricingCard className={plan.popular ? 'popular' : ''}>
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                      <Typography 
                        variant="h4" 
                        gutterBottom 
                        sx={{ fontWeight: 600, color: '#333' }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ color: '#666', mb: 3 }}
                      >
                        {plan.description}
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="h3" 
                          component="span"
                          sx={{ fontWeight: 800, color: '#0D47A1' }}
                        >
                          {plan.price === 'Özel' ? plan.price : `₺${plan.price}`}
                        </Typography>
                        <Typography 
                          variant="h6" 
                          component="span"
                          sx={{ color: '#666' }}
                        >
                          {plan.period}
                        </Typography>
                      </Box>
                      <List sx={{ mb: 3 }}>
                        {plan.features.map((feature, featureIndex) => (
                          <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircle sx={{ color: '#4CAF50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature}
                              primaryTypographyProps={{ fontSize: '0.9rem' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        variant={plan.popular ? 'contained' : 'outlined'}
                        fullWidth
                        size="large"
                        onClick={() => (plan.action === 'register' ? openRoleSelection() : handleOpenContactModal(plan.name))}
                        sx={{
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600
                        }}
                      >
                        {plan.buttonText}
                      </Button>
                    </CardActions>
                  </PricingCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box id="features" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('ecommerce.features.title')}
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <FeatureCard>
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                      <Avatar
                        sx={{
                          backgroundColor: feature.color,
                          width: 64,
                          height: 64,
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ fontWeight: 600, color: '#333' }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ color: '#666', lineHeight: 1.6 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box id="faq" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="md">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('ecommerce.faq.title')}
            </Typography>
            {faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        </Box>

        {/* CTA Section */}
        <Box id="cta" sx={{ py: 8, backgroundColor: BRAND.colors.primary, color: 'white' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' }
              }}
            >
              {t('ecommerce.cta.title')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                backgroundColor: 'white',
                color: BRAND.colors.primary,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => openRoleSelection()}
            >
              {t('ecommerce.cta.button')}
            </Button>
          </Container>
        </Box>
        <Footer />
      </ContentWrapper>

      <StandardMarketingModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        titleId="ecommerce-contact-modal-title"
        title={
          <>
            {formData.selectedPlan} {t('ecommerce.modal.contactTitle')}
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {t('ecommerce.modal.contactDescription')}
          </Typography>
          <TextField
            name="nameSurname"
            label={t('ecommerce.modal.namePlaceholder')}
            value={formData.nameSurname}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 1.5 }}
          />
          <TextField
            name="email"
            label={t('ecommerce.modal.emailPlaceholder')}
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 1.5 }}
          />
          <TextField
            name="phone"
            label={t('ecommerce.modal.phonePlaceholder')}
            value={formData.phone}
            onChange={handleInputChange}
            fullWidth
            required
            margin="dense"
            sx={{ ...marketingModalTextFieldSx, mb: 2 }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              type="button"
              onClick={handleCloseContactModal}
              disabled={isSubmitting}
              sx={{ color: 'text.secondary' }}
            >
              {t('ecommerce.modal.cancelButton')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
              }
              sx={{
                bgcolor: '#0D47A1',
                borderRadius: '12px',
                fontWeight: 'bold',
                px: 3,
                '&:hover': { bgcolor: '#0A3C87' },
              }}
            >
              {isSubmitting
                ? t('ecommerce.modal.sendingButton')
                : t('ecommerce.modal.sendButton')}
            </Button>
          </Box>
        </form>
      </StandardMarketingModal>

      <StandardMarketingModal
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        titleId="ecommerce-success-modal-title"
        title={t('ecommerce.modal.successTitle')}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerce.modal.successMessage')}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={handleCloseSuccessModal}
            sx={{
              bgcolor: '#0D47A1',
              borderRadius: '12px',
              fontWeight: 'bold',
              py: 1.2,
              '&:hover': { bgcolor: '#0A3C87' },
            }}
          >
            {t('ecommerce.modal.okButton')}
          </Button>
        </Box>
      </StandardMarketingModal>

      {/* Hata Bildirimi */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* WhatsApp Floating Button */}
      <PublicAiChatWidget />
    </PageContainer>
  );
};

export default Ecommerce;
